import { createSlice, isAnyOf } from '@reduxjs/toolkit'
import { getShouldCostModelBreakdownAsync } from 'src/services/costModelling/shouldCostLibrary.services'
import { getSimulationBOMForEditAsync } from 'src/services/costModelling/skuShouldCostWorkbench.services'
import { transformData } from 'src/views/pages/costModelling/utils/transformCostBreakdownData'

// Function to update the duty and tariff percentage based on the sum of other components (
// exludling duty and tariff and logistics)
function calculatedDutyTariffPercentage(costBreakdownData) {
  const dutyTariffComponent = costBreakdownData.find((item) => item.sectionId === 6)
  if (!dutyTariffComponent) return costBreakdownData

  const dutyPercentage = parseFloat(dutyTariffComponent?.data?.[0]?.percentage || 0)
  const tariffPercentage = parseFloat(dutyTariffComponent?.data?.[1]?.percentage || 0)
  const sumDutyTariff = dutyPercentage + tariffPercentage

  const sumOfFiveComponents = costBreakdownData.reduce((sum, item) => {
    const sectionId = item?.sectionId
    const percentage = parseFloat(item?.percentage || 0)
    if (sectionId !== 5 && sectionId !== 6) {
      return sum + percentage
    }
    return sum
  }, 0)

  return parseFloat(((sumDutyTariff / 100) * sumOfFiveComponents).toFixed(2)) || 0
}

function updateDutyTariffPercentage(
  costBreakdownData,
  adjustedDutyTariffPercentage,
  isZeroAccepted = false,
  isSimulation = false,
) {
  const calculatedPercentage = calculatedDutyTariffPercentage(costBreakdownData)

  return costBreakdownData.map((item) => {
    if (item.sectionId === 6) {
      if (isZeroAccepted && adjustedDutyTariffPercentage === 0) {
        return { ...item, percentage: adjustedDutyTariffPercentage ?? calculatedPercentage }
      }
      if (isSimulation) {
        return { ...item, percentage: adjustedDutyTariffPercentage }
      } else {
        return { ...item, percentage: adjustedDutyTariffPercentage || calculatedPercentage }
      }
    }
    return item
  })
}

const initialState = {
  costBreakdownData: [],
  costBreakDownLoader: false,
  subComponentBreakdownData: [],
  totalPercentage: 0,
  adjustedDutyTariffPercentage: 0, // This will be used to set the duty and tariff percentage when user edits it in the UI
  selectedComponentId: null,
  totalCost: 0,
  baseModelTotalCost: 0,
  initialCostBreakdownData: [],
  simulationFormula: {},
  prevCostBreakdownData:[]
}

export const getShouldCostBreakdownSlice = createSlice({
  name: 'cost-components-slice',
  initialState,
  extraReducers: (builder) => {
    builder.addMatcher(isAnyOf(getShouldCostModelBreakdownAsync.pending), (state) => {
      state.costBreakDownLoader = true
    })
    builder.addMatcher(isAnyOf(getShouldCostModelBreakdownAsync.fulfilled), (state, action) => {
      if (action.payload.data.length > 0) {
        const componentData = transformData(action?.payload?.data)
        state.costBreakdownData = structuredClone(componentData)
        state.prevCostBreakdownData = structuredClone(componentData)
        state.initialCostBreakdownData = structuredClone(componentData)

        const totalSumPercentage = componentData.reduce(
          (total, item) => total + Number(item.percentage),
          0,
        )
        state.totalPercentage = totalSumPercentage

        // if calculated and current duty tariff are same then it will be considered as calulated value else adjusted value
        const calulatedDutyTariff = calculatedDutyTariffPercentage(componentData)
        const currentDutyTariff = componentData?.find((item) => item.sectionId === 6)?.percentage
        if (calulatedDutyTariff === currentDutyTariff) {
          state.adjustedDutyTariffPercentage = 0
        } else {
          state.adjustedDutyTariffPercentage = currentDutyTariff || 0
        }
      } else {
        state.costBreakdownData = []
        state.costBreakDownLoader = false
        state.subComponentBreakdownData = []
      }

      state.costBreakDownLoader = false
    })
    builder.addMatcher(isAnyOf(getShouldCostModelBreakdownAsync.rejected), (state, action) => {
      state.costBreakDownLoader = false
    })

    // what-if-analysis
    builder.addMatcher(isAnyOf(getSimulationBOMForEditAsync.pending), (state) => {
      state.costBreakDownLoader = true
    })
    builder.addMatcher(isAnyOf(getSimulationBOMForEditAsync.fulfilled), (state, action) => {
      if (action.payload.data?.bill_of_material?.length > 0) {
        const componentData = transformData(action.payload.data?.bill_of_material)
        state.costBreakdownData = [...componentData]
        state.initialCostBreakdownData = [...componentData]

        const totalSumPercentage = componentData.reduce(
          (total, item) => total + Number(item.percentage),
          0,
        )
        state.selectedComponentId = componentData[0]?.sectionId || 1
        state.totalPercentage = totalSumPercentage
        state.totalCost = action.payload.data?.simulation_cost || 0
        state.simulationFormula = action.payload.data?.formula || {}
        state.baseModelTotalCost = action.payload.data?.base_model_cost || 0
        state.adjustedDutyTariffPercentage =
          componentData?.find((item) => item.sectionId === 6)?.percentage || 0
      } else {
        state.costBreakdownData = []
        state.costBreakDownLoader = false
        state.subComponentBreakdownData = []
      }

      state.costBreakDownLoader = false
    })
    builder.addMatcher(isAnyOf(getSimulationBOMForEditAsync.rejected), (state, action) => {
      state.costBreakDownLoader = false
    })
  },
  reducers: {
    addCostBreakdownComponent: (state, action) => {
      state.costBreakdownData = updateDutyTariffPercentage(
        [...state.costBreakdownData, action.payload],
        state.adjustedDutyTariffPercentage,
      )
    },

    removeCostBreakdownComponent: (state, action) => {
      const temp = state.costBreakdownData?.filter((obj) => obj.label !== action?.payload)

      if (action.payload === 'Duty & Tariff') {
        state.adjustedDutyTariffPercentage = 0
      }
      state.costBreakdownData = updateDutyTariffPercentage(temp, state.adjustedDutyTariffPercentage)
    },

    setCostBreakdownData: (state, action) => {
      // isZeroAccepted flag is used true when we are editing in simulation and false when we are editing/creating model
      const payload = action.payload
      const { breakdownData, isZeroAccepted = false } = Array.isArray(payload)
        ? { breakdownData: payload, isZeroAccepted: false }
        : payload
      const temp = updateDutyTariffPercentage(
        breakdownData,
        state.adjustedDutyTariffPercentage,
        isZeroAccepted,
      )
      state.costBreakdownData = temp
    },

    setSubComponentBreakdownData: (state, action) => {
      state.subComponentBreakdownData = action?.payload
    },

    setTotalPercentage: (state, action) => {
      state.totalPercentage = action?.payload
    },

    resetCostBreakdownData: () => initialState,

    resetSubComponentBreakdownData: () => initialState,
    setAdjustedDutyTariffPercentage: (state, action) => {
      // input check for DT calculation
      const adjustedValue =
        action.payload && typeof action.payload === 'object'
          ? action.payload.rawValue
          : action.payload

      state.adjustedDutyTariffPercentage = adjustedValue
      state.costBreakdownData = updateDutyTariffPercentage(
        state.costBreakdownData,
        adjustedValue,
        false,
        action.payload?.isSimulation || false,
      )
    },

    setSelectedComponentId: (state, action) => {
      state.selectedComponentId = action?.payload
    },

    setTotalCost: (state, action) => {
      state.totalCost = action?.payload
    },
  },
})

export const {
  addCostBreakdownComponent,
  removeCostBreakdownComponent,
  setCostBreakdownData,
  setSubComponentBreakdownData,
  setTotalPercentage,
  resetCostBreakdownData,
  resetSubComponentBreakdownData,
  setAdjustedDutyTariffPercentage,
  setSelectedComponentId,
  setTotalCost,
} = getShouldCostBreakdownSlice.actions

export default getShouldCostBreakdownSlice.reducer
